/**
 * useTodayWidgetsBundle — prime the three right-rail widget caches from
 * the Today route loader's pre-fetched super-bundle.
 *
 * Why this exists:
 *   Today view's right-rail widgets (RealitySnapshot velocity,
 *   StakeholderHeat, PendencyRadar) each call useResource with their
 *   own endpoint, firing three independent network requests on mount.
 *   The route loader (`todayLoader`) already fetches all three in
 *   parallel server-side via `/api/today/widgets`. This hook reads
 *   that loader-provided bundle and primes the three useResource
 *   caches SYNCHRONOUSLY during the parent's render — so by the time
 *   each child widget mounts and calls useResource, the cache is
 *   already populated and the widget reads from cache instead of
 *   firing its own request.
 *
 * Right-rail round trips: 3 → 0 on the initial paint.
 *
 * Subsequent refreshes (from `invalidate('stakeholder-heat')`,
 * `polkdesq:data-mutated`, etc.) still go through the widget's own
 * useResource fetcher — the prime cache is single-use per key.
 *
 * Note this hook returns nothing. The priming side-effect IS the
 * contract.
 */
import { useRef } from 'react';
import { primeResource } from './useResource';

export function useTodayWidgetsBundle(loaderData) {
  // Prime exactly once per Today-view mount. A ref-flag is the simplest
  // way to gate the synchronous side-effect from re-firing on every
  // re-render (which would re-prime stale data and force unnecessary
  // refetches in children).
  const primedRef = useRef(false);
  if (primedRef.current) return;
  const widgets = loaderData?.widgets;
  if (!widgets) return;
  primedRef.current = true;

  // Keys here must match the exact useResource keys used by the three
  // consuming widgets so the prime is picked up:
  //   RealitySnapshot.js → 'reality-snapshot-velocity'
  //   StakeholderHeat.js → 'stakeholder-heat'
  //   PendencyRadar.js   → 'pendency-radar-budgets'
  if (widgets.velocity)        primeResource('reality-snapshot-velocity', widgets.velocity);
  if (widgets.stakeholderHeat) primeResource('stakeholder-heat', widgets.stakeholderHeat);
  if (widgets.attentionBudget) primeResource('pendency-radar-budgets', widgets.attentionBudget);
}
